import { useMemo, useCallback } from 'react'
import { useResizeDetector } from 'react-resize-detector'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { PieChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'

import {
  TooltipComponent,
  GridComponent,
  LegendComponent,
} from 'echarts/components'

import { Box, BoxProps, Alert, Stack, Typography } from '@mui/material'

import { SisetuDoctorsConditions } from '../condition'
import { MemberClassStaticticsRecord } from './data'

type PieGraphItemStyle = {
  opacity?: number
}
const selectedStyle: PieGraphItemStyle = {
}
const notSelectedStyle: PieGraphItemStyle = {
  opacity: 0.3,
}

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  PieChart,
  CanvasRenderer,
])

type KaiinGraphProps = {
  sisetuCode: string
  dataList?: MemberClassStaticticsRecord[]
  condition: SisetuDoctorsConditions
  onChangeCondition: (axis: 'memberClass', condition: { code: string, name: string }) => void
} & BoxProps
export const KaiinGraph = ({
  dataList,
  condition,
  onChangeCondition,
  ...others
}: KaiinGraphProps) => {
  const { width, height, ref } = useResizeDetector()

  const summary = useMemo<{
    key: string
    name: string
    value: number
    itemStyle?: PieGraphItemStyle
  }[]>(
    () => {
      const summary = dataList?.map((record) => ({
        key: record.memberClassCode,
        name: record.memberClassName,
        value: record.kojinCount,
        itemStyle: condition.memberClass ? (
          condition.memberClass.code === record.memberClassCode ? selectedStyle : notSelectedStyle
        ) : undefined,
      })) || []
      summary.sort((a, b) => a.key.localeCompare(b.key))
      return summary
    },
    [dataList, condition.memberClass]
  )

  const onClickPie = useCallback(
    (params?: { data?: { key?: string, name?: string }}) => {
      const data = params?.data
      if (data && data.key) {
        onChangeCondition('memberClass', { code: data.key, name: data.name ?? '' })
      }
    },
    [onChangeCondition]
  )

  return (
    <Box ref={ref} width="100%" height="100%" position="relative" {...others}>
      <Stack
        direction="column"
        sx={{
          position: 'absolute',
          zIndex: 2,
          boxSizing: 'border-box',
          width: '100%'
        }}
        padding={1}
        spacing={1}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography
            variant="body1"
            component="div"
            color="text.primary"
            sx={{ textAlign: 'left', flexGrow: 1 }}
          >
            CN会員属性
          </Typography>
        </Stack>
        {dataList?.length === 0 && (
          <Alert severity="info">対象が見つかりません.</Alert>
        )}
      </Stack>

      {width && height && (
        <ReactEChartsCore
          echarts={echarts}
          notMerge={true}
          onEvents={{
            click: onClickPie,
          }}
          option={{
            //animation: false,
            tooltip: {
              trigger: 'item',
              valueFormatter: (value: number | string) => `${value}人`,
            },
            legend: {
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: 10,
              bottom: 10,
            },
            series: {
              type: 'pie',
              radius: '85%',
              center: ['40%', '50%'],
              label: {
                show: true,
                formatter: '{b}: {c}人',
                position: 'inside',
              },
              data: summary || [],
            },
            textStyle: {
              fontFamily: 'Noto Sans JP,sans-serif',
            },
          }}
          opts={{
            renderer: 'canvas',
            width: 'auto'
          }}
          style={{
            width, height
          }}
          theme="insite"
        />
      )}
    </Box>
  )
}
